import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchWithAuth } from '../api/fetchWithAuth';
import * as authApi from '../api/authApi';
import * as likesApi from '../api/likesApi';
import * as cartApi from '../api/cartApi';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  // initialize token and cached user from localStorage for immediate persistence
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
      try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    });
  const [loading, setLoading] = useState(false);

  const setToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
      setAuthToken(token);
    } else {
      localStorage.removeItem('token');
      setAuthToken(null);
    }
  };

  // helper to persist user object
  const saveUser = (u) => {
    setUser(u);
    try { localStorage.setItem('user', JSON.stringify(u)); } catch (e) { /* ignore */ }
  };

  // cart / likes / history / coupons helpers
  const addToCart = async (item) => {
    if (!item) return;

    const u = user || { cart: [], likes: [], purchaseHistory: [], coupons: [] };

    // Check if item is already in cart
    const isAlreadyInCart = Array.isArray(u.cart) && u.cart.some(cartItem => cartItem?.id === item?.id);
    if (isAlreadyInCart) {
      // Item already in cart, don't add again
      return;
    }

    try {
      // call server add when we have user id and an item id
      if (user?.id && item?.id) {
        await cartApi.addCartItem(user.id, item.id);
      }
    } catch (err) {
      // don't block local update on server error
      // eslint-disable-next-line no-console
      console.warn('cart add API failed', err);
    }

    const cart = Array.isArray(u.cart) ? [...u.cart, item] : [item];
    const cartIds = cart.map(i => i?.id).filter(Boolean);
    saveUser({ ...u, cart, cartIds });
  };

  const removeFromCart = async (itemId) => {
    if (!user) return;
    // accept either id or whole item object
    const id = itemId && typeof itemId === 'object' ? itemId.id : itemId;
    try {
      // call server delete when we have user id and an item id
      if (user.id && id != null) {
        await cartApi.deleteCartItem(user.id, id);
      }
    } catch (err) {
      // don't block local update on server error
      // eslint-disable-next-line no-console
      console.warn('cart delete API failed', err);
    }

    let cart = [];
    let cartIds = [];
    if (id != null) {
      cart = (user.cart || []).filter(i => String(i?.id) !== String(id));
      cartIds = cart.map(i => i?.id).filter(Boolean);
    } else {
      // fallback: try to remove by reference or shallow match
      cart = [...(user.cart || [])];
      const idx = cart.findIndex(i => i === itemId || JSON.stringify(i) === JSON.stringify(itemId));
      if (idx >= 0) cart.splice(idx, 1);
      cartIds = cart.map(i => i?.id).filter(Boolean);
    }
    saveUser({ ...user, cart, cartIds });
  };

  const addFavorite = async (item) => {
    if (!item) return;
    try {
      if (user?.id && item?.id) {
        await likesApi.addLike(user.id, item.id);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('addLike failed', err);
    }

    const u = user || { cart: [], likes: [], purchaseHistory: [], coupons: [] };
    const likes = Array.isArray(u.likes) ? [...u.likes, item] : [item];
    const likeIds = likes.map(i => i?.id).filter(Boolean);
    saveUser({ ...u, likes, likeIds });
  };

  const removeFavorite = async (itemId) => {
    if (!user) return;
    const id = itemId && typeof itemId === 'object' ? itemId.id : itemId;
    try {
      if (user.id && id != null) {
        await likesApi.deleteLike(user.id, id);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('deleteLike failed', err);
    }

    let likes = [];
    let likeIds = [];
    if (id != null) {
      likes = (user.likes || []).filter(i => String(i?.id) !== String(id));
      likeIds = likes.map(i => i?.id).filter(Boolean);
    } else {
      likes = [...(user.likes || [])];
      const idx = likes.findIndex(i => i === itemId || JSON.stringify(i) === JSON.stringify(itemId));
      if (idx >= 0) likes.splice(idx, 1);
      likeIds = likes.map(i => i?.id).filter(Boolean);
    }
    saveUser({ ...user, likes, likeIds });
  };

  const addPurchase = (purchase) => {
    const u = user || { cart: [], likes: [], purchaseHistory: [], coupons: [] };
    const purchaseHistory = Array.isArray(u.purchaseHistory) ? [...u.purchaseHistory, purchase] : [purchase];
    saveUser({ ...u, purchaseHistory });
  };

  // Load full cart items from server and update context
  const loadCart = useCallback(async () => {
    if (!user) return [];
    try {
      const items = await cartApi.getCart(user.id);
      // server should return array of items
      const cartItems = Array.isArray(items) ? items : [];
      const cartIds = cartItems.map(i => i?.id).filter(Boolean);
      saveUser({ ...user, cart: cartItems, cartIds });
      return cartItems;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('loadCart failed', err);
      return [];
    }
  }, [user]);

  // Load full liked items from server and update context (plus liked IDs)
  const loadLikes = useCallback(async () => {
    if (!user) return [];
    try {
      const items = await likesApi.getLikes(user.id);
      const likesItems = Array.isArray(items) ? items : [];
      const likeIds = likesItems.map(i => i?.id).filter(Boolean);
      saveUser({ ...user, likes: likesItems, likeIds });
      return likesItems;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('loadLikes failed', err);
      return [];
    }
  }, [user]);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    // expect { user, token }
    const token = data.token || data.accessToken || data.access_token || data.jwt;
    if (token && data.user) {
      setToken(token);
      // ensure default arrays present on user
      const u = { cart: [], likes: [], purchaseHistory: [], coupons: [], ...data.user };
      setUser(u);
        // persist cached user so page refresh shows logged-in immediately
        try { localStorage.setItem('user', JSON.stringify(u)); } catch (e) { /* ignore */ }
    }
    return data;
  };

  const register = async (name, email, password) => {
    const data = await authApi.register(name, email, password);
    const token = data.token || data.accessToken || data.access_token || data.jwt;
    if (token && data.user) {
      setToken(token);
      const u = { cart: [], likes: [], purchaseHistory: [], coupons: [], ...data.user };
      setUser(u);
        try { localStorage.setItem('user', JSON.stringify(u)); } catch (e) { /* ignore */ }
    }
    return data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
      try { localStorage.removeItem('user'); } catch (e) {}
  };

  // const loadProfile = async () => {
  //   const token = localStorage.getItem('token');
  //   if (!token) return null;
  //   setLoading(true);
  //   try {
  //     const res = await fetchWithAuth('/api/users/profile');
  //     if (!res.ok) {
  //       if (res.status === 401) {
  //         // invalid token
  //         setToken(null);
  //         setUser(null);
  //         return null;
  //       }
  //       throw new Error(`Profile fetch failed: ${res.status}`);
  //     }
  //     const data = await res.json();
  //     // server may return 'user' or the user object directly
  //     const userData = (data && data.user) ? data.user : data;
  //     const u = { cart: [], likes: [], purchaseHistory: [], coupons: [], ...userData };
  //     setUser(u);
  //       try { localStorage.setItem('user', JSON.stringify(u)); } catch (e) { /* ignore */ }
  //     return data;
  //   } catch (err) {
  //     // network or unexpected error – don't remove token automatically (avoid logging user out on transient network issues)
  //     // only remove token when server explicitly returns 401 above
  //     // eslint-disable-next-line no-console
  //     console.error('loadProfile failed', err);
  //     // keep cached user (if present) instead of clearing it here so refresh won't log out on transient failures
  //     return null;
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   loadProfile();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <UserContext.Provider value={{ user, token: authToken, isAuthenticated: !!user, loading, login, register, logout,
    // loadProfile,
     setToken, addToCart, removeFromCart, addFavorite, removeFavorite, addPurchase, loadCart, loadLikes }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
};

export default UserContext;